// frontend/src/api/ingestion.js

async function parseResponse(res) {
  const contentType = res.headers.get("content-type") || "";
  const isJson = contentType.includes("application/json");
  const payload = isJson ? await res.json() : await res.text();

  if (!res.ok) {
    const detail = isJson && payload ? payload.detail : null;
    const message =
      (isJson && payload && payload.message) ||
      (typeof detail === "string" && detail) ||
      (detail && typeof detail === "object" && (detail.error || JSON.stringify(detail))) ||
      (typeof payload === "string" && payload) ||
      `${res.status} ${res.statusText}`;
    throw new Error(message);
  }

  return payload;
}

/**
 * POST /api/ingestion/wipe-reingest
 * Deletes the connector's ENTIRE pipeline corpus — searchable_* index rows,
 * detected/extracted documents, loaded and discovered files — then queues a fresh
 * ingest request. Returns deleted_rows (the true total) and deleted_by_table.
 * If connectorId is provided it targets that specific connector; otherwise the
 * backend auto-selects the preferred one.
 */
export async function wipeReingest(orgId, connectorId = null) {
  const body = { org_id: orgId };
  if (connectorId != null) body.connector_id = connectorId;
  const res = await fetch("/api/ingestion/wipe-reingest", {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
  return parseResponse(res);
}

/**
 * POST /api/ingestion/ingest-new
 * Inserts a new ingest_request carrying the previous request's metadata.
 * If connectorId is provided it targets that specific connector; otherwise the
 * backend auto-selects the preferred one.
 */
export async function ingestNew(orgId, connectorId = null) {
  const body = { org_id: orgId };
  if (connectorId != null) body.connector_id = connectorId;
  const res = await fetch("/api/ingestion/ingest-new", {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
  return parseResponse(res);
}

/**
 * GET /api/ingestion/requests?org_id=X[&limit=N]
 * Returns every ingest request for the org, newest first, for the request picker.
 * Each row carries connector_id/name/type, file_count, last_activity, rank, and
 * `actionable` — whether the request is recent enough to accept mutations.
 */
export async function getIngestRequests(orgId, limit = 200) {
  const params = new URLSearchParams({ org_id: orgId, limit });
  const res = await fetch(`/api/ingestion/requests?${params}`, {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  return parseResponse(res);
}

/**
 * GET /api/ingestion/errors?org_id=X[&ingest_request_id=Y]
 * Returns all files with status='error' for one ingest request. Omitting the
 * request id tracks whichever request is latest (is_live=true in the response).
 * Each row includes error_at (ISO timestamp of when the failure occurred).
 */
export async function getIngestionErrors(orgId, ingestRequestId = null) {
  const params = new URLSearchParams({ org_id: orgId });
  if (ingestRequestId != null) params.set("ingest_request_id", ingestRequestId);
  const res = await fetch(`/api/ingestion/errors?${params}`, {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  return parseResponse(res);
}

/**
 * GET /api/ingestion/skipped?org_id=X[&ingest_request_id=Y]
 * Returns all files at status='skipped' for one ingest request. Each row carries
 * skip_reason ('Unsupported type' | 'Oversized') and `retryable` — only oversized
 * skips can succeed on a re-run. Response also carries counts_by_reason.
 */
export async function getSkippedFiles(orgId, ingestRequestId = null) {
  const params = new URLSearchParams({ org_id: orgId });
  if (ingestRequestId != null) params.set("ingest_request_id", ingestRequestId);
  const res = await fetch(`/api/ingestion/skipped?${params}`, {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  return parseResponse(res);
}

/**
 * GET /api/ingestion/stuck?org_id=X[&ingest_request_id=Y&threshold_minutes=N]
 * Returns files a stage finished that the next stage never picked up — no error, no
 * skip, just a gap. counts_by_stage is the exact total per gap; `rows` is capped at
 * limit_per_stage and `truncated` says whether anything was withheld.
 */
export async function getStuckFiles(orgId, ingestRequestId = null, thresholdMinutes = 10) {
  const params = new URLSearchParams({ org_id: orgId, threshold_minutes: thresholdMinutes });
  if (ingestRequestId != null) params.set("ingest_request_id", ingestRequestId);
  const res = await fetch(`/api/ingestion/stuck?${params}`, {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  return parseResponse(res);
}

function retryBody(orgId, { ingestRequestId = null, rows = null, layers = null }) {
  const body = { org_id: orgId };
  // Always pin the request explicitly: the operator is acting on the rows currently
  // on screen, and "whatever is latest" may have moved on since the page rendered.
  if (ingestRequestId != null) body.ingest_request_id = ingestRequestId;
  if (rows != null) body.rows = rows;
  if (layers != null) body.layers = layers;
  return body;
}

async function postRetry(path, body) {
  const res = await fetch(path, {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
  return parseResponse(res);
}

/**
 * POST /api/ingestion/retry-errors
 * Resets errored files back to 'new'. Pass exactly one of:
 *   rows:   [{layer, filepath, error_message, error_at}] — this exact selection.
 *           All three identifying fields are sent so the server resets only the row
 *           the operator saw, not a newer failure for the same path.
 *   layers: ["loaded_files", ...] — everything retryable in those stages.
 * Rows the server classifies as permanently failed are refused and reported in
 * dropped_non_retryable.
 */
export async function retryErrors(orgId, opts) {
  return postRetry("/api/ingestion/retry-errors", retryBody(orgId, opts));
}

/**
 * POST /api/ingestion/retry-skipped
 * Same shape as retryErrors, but resets status='skipped' rows. Restricted server-side
 * to the 'Oversized' bucket — unsupported file types are never reset.
 */
export async function retrySkipped(orgId, opts) {
  return postRetry("/api/ingestion/retry-skipped", retryBody(orgId, opts));
}

/**
 * GET /api/ingestion/status?org_id=X[&ingest_request_id=Y]
 * Returns per-layer ingestion counts for one ingest request. Omitting the request
 * id tracks whichever request is latest (is_live=true in the response). Response
 * also includes timestamps {layer: ISO-string}, `actionable`, and `error`.
 */
export async function getIngestionStatus(orgId, ingestRequestId = null) {
  const params = new URLSearchParams({ org_id: orgId });
  if (ingestRequestId != null) params.set("ingest_request_id", ingestRequestId);
  const res = await fetch(`/api/ingestion/status?${params}`, {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  return parseResponse(res);
}

/**
 * POST /api/ingestion/retry-stuck
 * Resets stuck files to 'new' at the stage that already finished them. Pass `items`
 * [{filepath, stuck_at}] for a selection, or omit for every stuck file. thresholdMinutes
 * must match the listing's, or the reset acts on a different set than was displayed.
 */
export async function retryStuck(orgId, { ingestRequestId = null, items = null, thresholdMinutes = 10 }) {
  const body = { org_id: orgId, threshold_minutes: thresholdMinutes };
  if (ingestRequestId != null) body.ingest_request_id = ingestRequestId;
  if (items != null) body.items = items;
  return postRetry("/api/ingestion/retry-stuck", body);
}

/**
 * GET /api/ingestion/connector?org_id=X
 * Returns all connectors for the org.
 */
export async function getConnectors(orgId) {
  const res = await fetch(`/api/ingestion/connector?org_id=${orgId}`, {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  return parseResponse(res);
}

/**
 * POST /api/ingestion/connector
 * Creates the azure-blob-storage connector with the given connection string.
 */
export async function createConnector(orgId, connectionString) {
  const res = await fetch("/api/ingestion/connector", {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ org_id: orgId, connection_string: connectionString }),
  });
  return parseResponse(res);
}
